import { useState } from 'react'
import { useRecipes } from '../hooks/useRecipes'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { CookMode } from './CookMode'

const StarRating = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onChange?.(i)}>
        <svg width="18" height="18" viewBox="0 0 24 24"
          fill={i <= value ? '#f59e0b' : 'none'}
          stroke={i <= value ? '#f59e0b' : '#d1d5db'}
          strokeWidth="1.5"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      </button>
    ))}
  </div>
)

export const RecipeDetail = ({ recipe, onEdit, onClose }) => {
  const { deleteRecipe, updateRecipe } = useRecipes()
  const [cookMode,      setCookMode]      = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting,      setDeleting]      = useState(false)
  const [rating,        setRating]        = useState(recipe.rating ?? 0)

  const handleRating = async (val) => {
    setRating(val)
    await updateRecipe({ id: recipe.id, rating: val })
  }

  const handleDelete = async () => {
    setDeleting(true)
    try { await deleteRecipe(recipe.id); onClose() }
    finally { setDeleting(false) }
  }

  const ingredients = recipe.ingredients ?? []
  const steps       = Array.isArray(recipe.steps)
    ? recipe.steps.map((s) => (typeof s === 'string' ? s : s.text ?? s.instruction ?? ''))
    : []

  if (cookMode) return <CookMode recipe={recipe} onClose={() => setCookMode(false)} />

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Photo */}
        {recipe.photo_url && (
          <div className="h-40 overflow-hidden rounded-t-2xl flex-shrink-0">
            <img src={recipe.photo_url} alt={recipe.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-start justify-between px-5 pt-4 pb-2">
          <h2 className="text-xl font-bold text-text-primary flex-1 pr-4 leading-tight">{recipe.title}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary p-1 flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-4 px-5 pb-3">
          <StarRating value={rating} onChange={handleRating} />
          {recipe.prep_time_minutes && (
            <span className="text-xs text-text-muted">Prep {recipe.prep_time_minutes}m</span>
          )}
          {recipe.cook_time_minutes && (
            <span className="text-xs text-text-muted">Cook {recipe.cook_time_minutes}m</span>
          )}
          {recipe.servings && (
            <span className="text-xs text-text-muted">{recipe.servings} servings</span>
          )}
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 pb-4 space-y-5">
          {recipe.description && (
            <p className="text-sm text-text-secondary">{recipe.description}</p>
          )}

          {ingredients.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Ingredients</h3>
              <ul className="space-y-1">
                {ingredients.map((ing, i) => (
                  <li key={i} className="flex items-baseline gap-2 text-sm text-text-secondary">
                    <span className="text-text-muted w-16 flex-shrink-0 text-xs">
                      {[ing.amount, ing.unit].filter(Boolean).join(' ')}
                    </span>
                    <span>{ing.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {steps.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Instructions</h3>
              <ol className="space-y-3">
                {steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-xs font-bold text-primary bg-primary-light w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-text-secondary leading-relaxed">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2">
          {confirmDelete ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)} className="flex-1">Cancel</Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting} className="flex-1">
                {deleting ? <Spinner size="sm" /> : 'Delete'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} className="flex-shrink-0">Delete</Button>
              <Button variant="secondary" size="sm" onClick={() => onEdit(recipe)} className="flex-1">Edit</Button>
              <Button size="sm" onClick={() => setCookMode(true)} className="flex-1">Cook Mode</Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
